import { defineStore } from 'pinia';
import { Config } from '@/config';
import apiService from '@/services/api.service';
import type {
  DocumentSubmissionDetailDto,
  DocumentSubmissionFilter,
  DocumentSubmissionListItemDto,
  DocumentSubmissionPublishDto,
  DocumentSubmissionRefuseDto,
} from '@/services/types/api.types';

export interface DocumentSubmissionsState {
  documentSubmissions: DocumentSubmissionListItemDto[];
  /** The filter `documentSubmissions` was loaded with, `null` when the list is empty after a failure. */
  documentSubmissionsFilter: DocumentSubmissionFilter | null;
  itemLoadingStates: Set<number>;
  lastListRequest: number;
}

export const useDocumentSubmissionsStore = defineStore({
  id: 'documentSubmissions',
  state: (): DocumentSubmissionsState => ({
    documentSubmissions: [],
    documentSubmissionsFilter: null,
    itemLoadingStates: new Set<number>(),
    lastListRequest: 0,
  }),
  getters: {
    isItemLoading: (state) => (projectModuleId: number) => state.itemLoadingStates.has(projectModuleId),
  },
  actions: {
    /**
     * The module's review list, filtered on the server. Only the latest request's answer is kept. `false` when that
     * request fails: the list is then emptied, so another filter's rows never show under this one.
     */
    async fetchDocumentSubmissionsByModuleId(projectModuleId: number, filter: DocumentSubmissionFilter) {
      const request = ++this.lastListRequest;
      this.itemLoadingStates.add(projectModuleId);
      try {
        const { data } = await apiService.get<DocumentSubmissionListItemDto[]>(
          `${Config.api.v2}/project-modules/${projectModuleId}/document-submissions`,
          { filter },
        );
        if (request === this.lastListRequest) {
          this.documentSubmissions = data;
          this.documentSubmissionsFilter = filter;
        }
        return true;
      } catch (error) {
        console.error(error);
        if (request !== this.lastListRequest) return true;
        this.documentSubmissions = [];
        this.documentSubmissionsFilter = null;
        return false;
      } finally {
        // A replaced request leaves the spinner to the one that replaced it.
        if (request === this.lastListRequest) this.itemLoadingStates.delete(projectModuleId);
      }
    },
    /** Swaps a document's place on the project page with its neighbour's. `false` when the server refuses. */
    async swapDocumentSubmissionOrder(documentSubmissionId: number, neighbourId: number) {
      try {
        await apiService.post(`${Config.api.v2}/document-submissions/${documentSubmissionId}/:reorder`, {
          withDocumentSubmissionId: neighbourId,
        });
        return true;
      } catch (error) {
        console.error(error);
        return false;
      }
    },
    /** One document with its participant and every version, or `null` when it cannot be loaded. */
    async fetchDocumentSubmissionById(documentSubmissionId: number) {
      try {
        const { data } = await apiService.get<DocumentSubmissionDetailDto>(
          `${Config.api.v2}/document-submissions/${documentSubmissionId}`,
        );
        return data;
      } catch (error) {
        console.error(error);
        return null;
      }
    },
    // The decisions answer the refreshed document, which the dialog shows as is. They rethrow, so the dialog can tell
    // a conflict (409: the document changed since it was opened) from any other failure, and never closes on one.
    /** Publishes a version, or publishes an approved one again in other languages. */
    async publishVersion(versionId: number, decision: DocumentSubmissionPublishDto) {
      const { data } = await apiService.post<DocumentSubmissionDetailDto>(
        `${Config.api.v2}/document-submission-versions/${versionId}/:publish`,
        decision,
      );
      return data;
    },
    /** Refuses the latest version waiting for review. */
    async refuseVersion(versionId: number, decision: DocumentSubmissionRefuseDto) {
      const { data } = await apiService.post<DocumentSubmissionDetailDto>(
        `${Config.api.v2}/document-submission-versions/${versionId}/:refuse`,
        decision,
      );
      return data;
    },
    /** Takes a document off the project page; its versions keep their status. */
    async unpublishDocument(documentSubmissionId: number) {
      const { data } = await apiService.post<DocumentSubmissionDetailDto>(
        `${Config.api.v2}/document-submissions/${documentSubmissionId}/:unpublish`,
      );
      return data;
    },
  },
});
